/**
 * Role Assignment API Service
 *
 * Handles Azure RBAC role assignment operations via the ARM control plane.
 * Used during Foundry agent connection creation to automatically grant
 * the Logic App's managed identity the permissions it needs to access
 * Foundry agents.
 *
 * FoundryAgentServiceV2 grants "Foundry User" on the selected project.
 * Legacy FoundryAgentService grants "Azure AI Developer" on the parent
 * Cognitive Services account.
 *
 * Without this role, the runtime gets:
 *   HTTP 401 PermissionDenied — principal lacks required data action
 */

import type { AgentModelType } from '@/types/agentConnection';
import {
  AGENT_MSI_REQUIRED_ROLE_DEFINITION_IDS,
  ROLE_AZURE_AI_DEVELOPER,
  ROLE_AZURE_AI_USER,
  ROLE_COGNITIVE_SERVICES_OPENAI_USER,
} from '@/types/hostedAgent';
import { toAccountArmResourceId } from '@/utils/connections';
import { apiVersions, getLogicEndpoint } from './endpoints';

// =============================================================================
// CONSTANTS
// =============================================================================

import { getArmBaseUrl, isArmMockMode } from './armConfig';

const AUTHORIZATION_API_VERSION = '2022-04-01';

/**
 * Well-known Azure built-in role definition GUIDs.
 *
 * "Foundry User" (53ca6127), formerly "Azure AI User", covers the Cognitive
 * Services data actions used by Foundry V2. "Azure AI Developer" covers the
 * legacy Azure Machine Learning workspace and Foundry hub path.
 */
const FOUNDRY_V1_ROLE_ID = ROLE_AZURE_AI_DEVELOPER;
const FOUNDRY_V2_ROLE_ID = ROLE_AZURE_AI_USER;
const ROLE_NAMES_BY_ID: Record<string, string> = {
  [ROLE_AZURE_AI_USER]: 'Foundry User',
  [ROLE_COGNITIVE_SERVICES_OPENAI_USER]:
    'Cognitive Services OpenAI User',
};

// =============================================================================
// TYPES
// =============================================================================

export interface RoleAssignmentResult {
  /** Whether the role assignment succeeded or was already in place */
  success: boolean;
  /** True if the role was already assigned (no action taken) */
  alreadyAssigned: boolean;
  /** Error message if the assignment failed */
  error?: string | undefined;
  /** Error code from the API */
  errorCode?: string | undefined;
  /** Role definition selected or found, when applicable */
  roleDefinitionId?: string | undefined;
  /** Display name of the selected or found role */
  roleName?: string | undefined;
  /** True when no compatible built-in definition exists in this cloud */
  skipped?: boolean | undefined;
}

interface SiteIdentity {
  principalId: string;
  tenantId: string;
  type: string;
}

interface SiteResponse {
  identity?: SiteIdentity;
}

interface RoleAssignmentProperties {
  roleDefinitionId: string;
  principalId: string;
  principalType?: string;
  scope?: string;
}

interface RoleAssignment {
  id: string;
  name: string;
  properties: RoleAssignmentProperties;
}

interface RoleAssignmentListResponse {
  value: RoleAssignment[];
  nextLink?: string;
}

interface RoleDefinition {
  id: string;
  name: string;
  properties?: {
    roleName?: string;
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Make an authenticated ARM request.
 */
async function armFetch<T>(
  url: string,
  accessToken: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (!isArmMockMode()) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: { error?: { code?: string; message?: string } } | null = null;
    try {
      errorData = await response.json();
    } catch {
      // Response might not be JSON
    }

    const error = new Error(
      errorData?.error?.message ?? `ARM request failed: ${response.status}`
    ) as Error & { code?: string | undefined; status?: number };
    error.code = errorData?.error?.code ?? undefined;
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Extract subscription ID from an ARM resource ID.
 */
function extractSubscriptionId(resourceId: string): string | undefined {
  const match = resourceId.match(/\/subscriptions\/([^/]+)/i);
  return match?.[1];
}

function normalizeResourceId(resourceId: string): string {
  return resourceId.replace(/\/+$/, '').toLowerCase();
}

function getRoleDefinitionGuid(roleDefinitionId: string): string | undefined {
  return roleDefinitionId.split('/').filter(Boolean).pop();
}

function getRoleAssignmentScope(assignment: RoleAssignment): string | undefined {
  if (assignment.properties.scope) {
    return assignment.properties.scope;
  }
  const marker = '/providers/microsoft.authorization/roleassignments/';
  const markerIndex = assignment.id.toLowerCase().lastIndexOf(marker);
  return markerIndex >= 0 ? assignment.id.substring(0, markerIndex) : undefined;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/** Get the automation application's system-assigned identity from the Otto data plane. */
export async function getLogicAppIdentity(
  projectName: string,
  appName: string,
  accessToken: string,
): Promise<SiteIdentity | null> {
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/applications/${encodeURIComponent(appName)}?api-version=${apiVersions.otto}`;
  const application = await armFetch<SiteResponse>(url, accessToken);
  return application.identity ?? null;
}

export interface ApplicationIdentityContext {
  projectName: string;
  appName: string;
  ottoToken?: string | undefined;
  principalId?: string | undefined;
}

interface RequiredRoleAssignment {
  scope: string;
  roleId: string;
  roleDescription: string;
  scopeDescription: string;
}

interface AvailableRoleDefinition {
  id: string;
  name: string;
}

async function listRoleAssignmentsForPrincipal(
  scope: string,
  principalId: string,
  accessToken: string,
): Promise<RoleAssignment[]> {
  const filter = encodeURIComponent(`principalId eq '${principalId}'`);
  let url: string | undefined =
    `${getArmBaseUrl()}${scope}/providers/Microsoft.Authorization/roleAssignments?api-version=${AUTHORIZATION_API_VERSION}&$filter=${filter}`;
  const assignments: RoleAssignment[] = [];

  while (url) {
    const result: RoleAssignmentListResponse =
      await armFetch<RoleAssignmentListResponse>(url, accessToken);
    assignments.push(...result.value);
    url = result.nextLink;
  }
  return assignments;
}

async function findExistingAcceptableRoleAssignment(
  scope: string,
  principalId: string,
  acceptableRoleDefinitionIds: readonly string[],
  accessToken: string,
): Promise<string | undefined> {
  const acceptableIds = new Set(
    acceptableRoleDefinitionIds.map((id) => id.toLowerCase()),
  );
  const normalizedScope = normalizeResourceId(scope);
  const assignments = await listRoleAssignmentsForPrincipal(
    scope,
    principalId,
    accessToken,
  );

  for (const assignment of assignments) {
    const assignmentScope = getRoleAssignmentScope(assignment);
    const roleId = getRoleDefinitionGuid(
      assignment.properties.roleDefinitionId,
    );
    if (
      assignmentScope &&
      normalizeResourceId(assignmentScope) === normalizedScope &&
      roleId &&
      acceptableIds.has(roleId.toLowerCase())
    ) {
      return roleId;
    }
  }
  return undefined;
}

async function resolveFirstAvailableRoleDefinition(
  scope: string,
  preferredRoleDefinitionIds: readonly string[],
  accessToken: string,
): Promise<AvailableRoleDefinition | undefined> {
  if (!extractSubscriptionId(scope)) {
    throw new Error('Cannot extract subscription ID from scope');
  }

  for (const roleDefinitionId of preferredRoleDefinitionIds) {
    const url =
      `${getArmBaseUrl()}${scope}` +
      `/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionId}` +
      `?api-version=${AUTHORIZATION_API_VERSION}`;
    try {
      const definition = await armFetch<RoleDefinition>(url, accessToken);
      return {
        id: roleDefinitionId,
        name:
          definition.properties?.roleName ??
          ROLE_NAMES_BY_ID[roleDefinitionId] ??
          roleDefinitionId,
      };
    } catch (error) {
      if ((error as Error & { status?: number }).status === 404) {
        continue;
      }
      throw error;
    }
  }
  return undefined;
}

/**
 * Check if a role assignment already exists for a given principal on a scope.
 */
export async function checkExistingRoleAssignment(
  scope: string,
  principalId: string,
  roleDefinitionGuid: string,
  accessToken: string
): Promise<boolean> {
  if (!extractSubscriptionId(scope)) return false;

  try {
    return !!(await findExistingAcceptableRoleAssignment(
      scope,
      principalId,
      [roleDefinitionGuid],
      accessToken,
    ));
  } catch {
    // If we can't check, assume not assigned (will try to assign)
    return false;
  }
}

async function ensurePreferredRoleAssignment(
    scope: string,
    preferredRoleDefinitionIds: readonly string[],
    accessToken: string,
    appIdentity: ApplicationIdentityContext,
  ): Promise<RoleAssignmentResult> {
    let operation = 'resolve the workflow app managed identity';
    try {
      const principalId = await resolveApplicationPrincipalId(appIdentity);
      if (!principalId) {
        return {
          success: false,
          alreadyAssigned: false,
          error:
            'Workflow app does not have a system-assigned managed identity enabled. ' +
            'Please enable it in the app Identity settings.',
          errorCode: 'NoManagedIdentity',
        };
      }

      operation = 'read existing role assignments on the Azure OpenAI account';
      const existingRoleId = await findExistingAcceptableRoleAssignment(
        scope,
        principalId,
        preferredRoleDefinitionIds,
        accessToken,
      );
      if (existingRoleId) {
        return {
          success: true,
          alreadyAssigned: true,
          roleDefinitionId: existingRoleId,
          roleName: ROLE_NAMES_BY_ID[existingRoleId] ?? existingRoleId,
        };
      }

      operation = 'resolve a compatible agent data-plane role definition';
      const selectedRole = await resolveFirstAvailableRoleDefinition(
        scope,
        preferredRoleDefinitionIds,
        accessToken,
      );
      if (!selectedRole) {
        return {
          success: true,
          alreadyAssigned: false,
          skipped: true,
        };
      }

      operation = `assign the ${selectedRole.name} role`;
      await createRoleAssignment(
        scope,
        principalId,
        selectedRole.id,
        accessToken,
      );
      return {
        success: true,
        alreadyAssigned: false,
        roleDefinitionId: selectedRole.id,
        roleName: selectedRole.name,
      };
    } catch (err) {
      const error = err as Error & { code?: string; status?: number };
      if (error.code === 'RoleAssignmentExists') {
        return { success: true, alreadyAssigned: true };
      }
      if (error.code === 'AuthorizationFailed' || error.status === 403) {
        const azureDetails =
          error.message && !error.message.startsWith('ARM request failed:')
            ? ` Azure response: ${error.message}`
            : '';
        return {
          success: false,
          alreadyAssigned: false,
          error:
            `Azure denied permission to ${operation}. ` +
            'Please ask an Owner or User Access Administrator to grant a compatible ' +
            'agent data-plane role (Foundry User or Cognitive Services OpenAI User) ' +
            "to your workflow app's managed identity on the Azure OpenAI account." +
            azureDetails,
          errorCode: 'InsufficientPermissions',
        };
      }
      return {
        success: false,
        alreadyAssigned: false,
        error: error.message ?? 'Failed to assign permissions',
        errorCode: error.code,
      };
  }
}

/**
 * Create a role assignment on a resource scope.
 */
export async function createRoleAssignment(
  scope: string,
  principalId: string,
  roleDefinitionGuid: string,
  accessToken: string
): Promise<void> {
  const subscriptionId = extractSubscriptionId(scope);
  if (!subscriptionId) {
    throw new Error('Cannot extract subscription ID from scope');
  }

  const roleAssignmentName = crypto.randomUUID();
  const roleDefinitionId = `/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionGuid}`;

  const url = `${getArmBaseUrl()}${scope}/providers/Microsoft.Authorization/roleAssignments/${roleAssignmentName}?api-version=${AUTHORIZATION_API_VERSION}`;

  await armFetch(url, accessToken, {
    method: 'PUT',
    body: JSON.stringify({
      properties: {
        roleDefinitionId,
        principalId,
        principalType: 'ServicePrincipal',
      },
    }),
  });
}

async function resolveApplicationPrincipalId(
  appIdentity: ApplicationIdentityContext,
): Promise<string | undefined> {
  if (appIdentity.principalId) {
    return appIdentity.principalId;
  }
  if (!appIdentity.ottoToken) {
    return undefined;
  }
  return (
    await getLogicAppIdentity(
      appIdentity.projectName,
      appIdentity.appName,
      appIdentity.ottoToken,
    )
  )?.principalId;
}

async function ensureRequiredRoleAssignment(
  requirement: RequiredRoleAssignment,
  accessToken: string,
  appIdentity: ApplicationIdentityContext,
): Promise<RoleAssignmentResult> {
  try {
    const principalId = await resolveApplicationPrincipalId(appIdentity);
    if (!principalId) {
      return {
        success: false,
        alreadyAssigned: false,
        error:
          'Workflow app does not have a system-assigned managed identity enabled. ' +
          'Please enable it in the app Identity settings.',
        errorCode: 'NoManagedIdentity',
      };
    }

    const alreadyAssigned = await checkExistingRoleAssignment(
      requirement.scope,
      principalId,
      requirement.roleId,
      accessToken,
    );
    if (alreadyAssigned) {
      return { success: true, alreadyAssigned: true };
    }

    await createRoleAssignment(
      requirement.scope,
      principalId,
      requirement.roleId,
      accessToken,
    );
    return { success: true, alreadyAssigned: false };
  } catch (err) {
    const error = err as Error & { code?: string; status?: number };
    if (error.code === 'RoleAssignmentExists') {
      return { success: true, alreadyAssigned: true };
    }
    if (error.code === 'AuthorizationFailed' || error.status === 403) {
      return {
        success: false,
        alreadyAssigned: false,
        error:
          'You do not have permission to assign roles on this resource. ' +
          'Please ask an Owner or User Access Administrator to grant the ' +
          `${requirement.roleDescription} to your workflow app's managed identity ` +
          `on the ${requirement.scopeDescription}.`,
        errorCode: 'InsufficientPermissions',
      };
    }
    return {
      success: false,
      alreadyAssigned: false,
      error: error.message ?? 'Failed to assign permissions',
      errorCode: error.code,
    };
  }
}

/**
 * Ensure the workflow application's SAMI can invoke models on an Azure OpenAI
 * account. Failure is returned to the caller so connection persistence can be
 * blocked instead of saving an unusable managed-identity connection.
 */
export async function ensureAzureOpenAIPermissions(
  accountResourceId: string,
  accessToken: string,
  appIdentity: ApplicationIdentityContext,
): Promise<RoleAssignmentResult> {
  return ensurePreferredRoleAssignment(
    toAccountArmResourceId(accountResourceId),
    AGENT_MSI_REQUIRED_ROLE_DEFINITION_IDS,
    accessToken,
    appIdentity,
  );
}

/**
 * Ensure the Logic App's managed identity has the role required by the selected
 * Foundry API.
 *
 * This is the main function called during Foundry connection creation.
 * It:
 *   1. Fetches the Logic App's system-assigned managed identity principal ID
 *   2. Checks the required role at the appropriate Foundry scope
 *   3. Creates the missing role assignment via ARM PUT
 *
 * @param projectResourceId - Full ARM ID of the Foundry project (or account)
 * @param agentModelType - Foundry API version selected for the connection
 * @param accessToken    - User's ARM bearer token
 */
export async function ensureFoundryPermissions(
  projectResourceId: string,
  agentModelType: AgentModelType,
  accessToken: string,
  appIdentity: ApplicationIdentityContext,
): Promise<RoleAssignmentResult> {
  const requiredRolesDescription = agentModelType === 'FoundryAgentServiceV2'
    ? '"Foundry User" role'
    : '"Azure AI Developer" role';
  const assignmentScopeDescription = agentModelType === 'FoundryAgentServiceV2'
    ? 'Foundry project'
    : 'Cognitive Services account';

  // Guard before any lookup: without project/app context the data-plane identity
  // call cannot resolve, so return an actionable error instead of a generic
  // "no managed identity" failure. The principalId fast path is unaffected.
  if (
    !appIdentity.principalId &&
    appIdentity.ottoToken &&
    (!appIdentity.projectName.trim() || !appIdentity.appName.trim())
  ) {
    return {
      success: false,
      alreadyAssigned: false,
      error:
        'Cannot look up the Logic App managed identity without an automation project and application. ' +
        'Please select an application and try again.',
      errorCode: 'MissingApplicationContext',
    };
  }

  const assignmentScope = agentModelType === 'FoundryAgentServiceV2'
    ? projectResourceId
    : toAccountArmResourceId(projectResourceId);
  const requiredRoleId = agentModelType === 'FoundryAgentServiceV2'
    ? FOUNDRY_V2_ROLE_ID
    : FOUNDRY_V1_ROLE_ID;

  return ensureRequiredRoleAssignment(
    {
      scope: assignmentScope,
      roleId: requiredRoleId,
      roleDescription: requiredRolesDescription,
      scopeDescription: assignmentScopeDescription,
    },
    accessToken,
    appIdentity,
  );
}
